import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useRef,
} from 'react';
import type { FC } from 'react';

import { useHistory } from 'react-router-dom';

import { useAppSelector } from '../modules/hooks';
import { selectCurrentlyPlayingAsset } from '../modules/ui';
import { useJWPlayerEvent } from '../components/utils/useJWPlayerEvent';
import { getPath, routeNames } from '../shared/helpers/routes';
import { getAssetIdFromLocation } from '../helpers/getAssetIdFromLocation';

interface PictureInPictureContextProps {
  isPipEnabled: boolean;
}

export const PictureInPictureContext =
  createContext<PictureInPictureContextProps>({
    isPipEnabled: false,
  });

export const PictureInPictureProvider: FC = ({ children }) => {
  const [isPipEnabled, setIsPipEnabled] = useState(false);

  const history = useHistory();
  const currentlyPlayingAsset = useAppSelector(selectCurrentlyPlayingAsset);

  const currentlyPlayingAssetRef = useRef(currentlyPlayingAsset);
  currentlyPlayingAssetRef.current = currentlyPlayingAsset;

  const handlePipEnter = useCallback(() => {
    setIsPipEnabled(true);
  }, []);

  const handlePipLeave = useCallback(() => {
    const matchedAssetId = getAssetIdFromLocation({
      location: history.location,
      vendor: currentlyPlayingAssetRef.current?.provider,
    });

    const hasAssetPageChanged =
      matchedAssetId !== currentlyPlayingAssetRef.current?.id;

    if (hasAssetPageChanged && currentlyPlayingAssetRef.current) {
      history.push(
        getPath(routeNames.asset, {
          asset: currentlyPlayingAssetRef.current,
        })
      );
    }

    // this needs to be done after history.push to guarantee that the playback will continue smoothly
    setIsPipEnabled(false);
  }, [history]);

  useJWPlayerEvent('pipEnter', handlePipEnter);
  useJWPlayerEvent('pipLeave', handlePipLeave);

  const state = useMemo(() => ({ isPipEnabled }), [isPipEnabled]);

  return (
    <PictureInPictureContext.Provider value={state}>
      {children}
    </PictureInPictureContext.Provider>
  );
};

export const usePipContext = () => {
  const pip = useContext(PictureInPictureContext);

  if (process.env.NODE_ENV !== 'production' && !pip) {
    throw new Error(
      'Picture-In-Picture context is undefined, please verify you are calling usePictureInPicture() as child of a <PictureInPictureProvider> component.'
    );
  }

  return pip;
};
